import React from "react";
import { AppError } from "@/lib/utils/errors";
import { NotificationService } from "@/modules/services/notification/notificationService";
import { NotificationMessageDto, NotificationObject } from "@/modules/types/notification";
import { useCallback } from "react";
import { useBusinessInfo } from "../business";
import { useSidebarStore } from "@/modules/store/sidebarStore";
import { useNotificationStore } from "@/modules/store/notificationStore";
import { useRouter } from "next/navigation";

export const useNotification = () => {
  const { isOpenSidebarRight: isShow, toggleSidebarRight } = useSidebarStore();
  const [index, setIndex] = React.useState(0);
  const {
    notReadNoti,
    allNoti,
    setLoadingNotReadNoti,
    setUnreadNoti,
    setLoadingAllNoti,
    setAllNoti,
    setSeenNoti,
  } = useNotificationStore();
  const notiSvc = new NotificationService();
  const { businessInfo } = useBusinessInfo();

  const loadUnreadNoti = async (isLoadMore: boolean = false) => {
    if (!businessInfo) return;
    try {
      setLoadingNotReadNoti(true);
      const res = await notiSvc.getNotifications(
        businessInfo.id,
        undefined,
        notReadNoti.limit,
        false,
        false,
        isLoadMore ? notReadNoti.nextId : undefined
      );
      if (res && res.notifications) {
        const hasMore =
          res.nextId !== undefined &&
          res.nextId !== null &&
          res.nextId !== "" &&
          res.notifications.length >= notReadNoti.limit;
        setUnreadNoti({
          notifications: res.notifications,
          total: res.total,
          nextId: res.nextId,
          hasMore,
          isAppend: isLoadMore,
        });
      } else {
        setUnreadNoti({
          notifications: isLoadMore ? [] : [],
          total: res?.total ?? 0,
          nextId: res?.nextId,
          hasMore: false,
          isAppend: isLoadMore,
        });
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
    } finally {
      setLoadingNotReadNoti(false);
    }
  };

  const loadAllNoti = async (isLoadMore: boolean = false) => {
    if (!businessInfo) return;
    try {
      setLoadingAllNoti(true);
      const res = await notiSvc.getNotifications(
        businessInfo.id,
        undefined,
        allNoti.limit,
        undefined,
        undefined,
        isLoadMore ? allNoti.nextId : undefined
      );
      if (res && res.notifications) {
        const hasMore =
          res.nextId !== undefined &&
          res.nextId !== null &&
          res.nextId !== "" &&
          res.notifications.length >= allNoti.limit;
        setAllNoti({
          notifications: res.notifications,
          total: res.total,
          nextId: res.nextId,
          hasMore,
          isAppend: isLoadMore,
        });
      } else {
        setAllNoti({
          notifications: isLoadMore ? [] : [],
          total: res?.total ?? 0,
          nextId: res?.nextId,
          hasMore: false,
          isAppend: isLoadMore,
        });
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
    } finally {
      setLoadingAllNoti(false);
    }
  };

  const toggleShow = useCallback(() => {
    if (isShow) {
      setIndex(0);
    }
    toggleSidebarRight();
  }, [isShow]);

  const onLoadMore = () => {
    switch (index) {
      case 0:
        loadUnreadNoti(true);
        break;
      case 1:
        loadAllNoti(true);
    }
  };

  const hasMore = () => {
    switch (index) {
      case 0:
        return notReadNoti.hasMore;
      case 1:
        return allNoti.hasMore;
      default:
        return false;
    }
  };

  return {
    isLoading: notReadNoti.isLoading || allNoti.isLoading,
    isLoadingUnread: notReadNoti.isLoading,
    isLoadingAll: allNoti.isLoading,
    isShow,
    index,
    unReadNoti: notReadNoti.notifications,
    allNoti: allNoti.notifications,
    totalUnread: notReadNoti.total,
    totalAllNoti: allNoti.total,
    toggleShow,
    setIndex,
    onLoadMore,
    hasMore,
    loadUnreadNoti,
    loadAllNoti,
  };
};

export const useNotificationItem = () => {
  const router = useRouter();
  const notiSvc = new NotificationService();
  const { businessInfo } = useBusinessInfo();
  const { setSeenNoti } = useNotificationStore();

  const generateContent = (noti: NotificationMessageDto) => {
    const content = noti.content;
    if (!content) return null;

    // Helper to convert byte index to character index
    const byteIndexToCharIndex = (str: string, byteIndex: number) => {
      const buf = new TextEncoder().encode(str);
      return new TextDecoder().decode(buf.slice(0, byteIndex)).length;
    };

    const highlights: { start: number; end: number }[] = [];

    const addHighlight = (obj?: NotificationObject) => {
      if (obj?.metadata && typeof obj.metadata.offset === "number" && typeof obj.metadata.length === "number") {
        const offset = obj.metadata.offset;
        const length = obj.metadata.length;
        if (length > 0) {
          const charStart = byteIndexToCharIndex(content, offset);
          const charEnd = byteIndexToCharIndex(content, offset + length);
          highlights.push({ start: charStart, end: charEnd });
        }
      }
    };

    // Collect all highlights
    if (noti.subject) {
      noti.subject.forEach(addHighlight);
    }
    addHighlight(noti.diObject);
    addHighlight(noti.inObject);
    addHighlight(noti.prObject);

    if (highlights.length === 0) {
      return (
        <p className="font-normal text-sm text-gray-600 dark:text-gray-300">
          {content}
        </p>
      );
    }

    // Sort highlights by start position
    highlights.sort((a, b) => a.start - b.start);

    // Merge overlapping highlights if any (optional safety)
    const merged: { start: number; end: number }[] = [];
    if (highlights.length > 0) {
      let current = highlights[0];
      for (let i = 1; i < highlights.length; i++) {
        if (highlights[i].start < current.end) {
          current.end = Math.max(current.end, highlights[i].end);
        } else {
          merged.push(current);
          current = highlights[i];
        }
      }
      merged.push(current);
    }

    // Build the content components
    const result: React.ReactNode[] = [];
    let lastIndex = 0;

    merged.forEach((h, i) => {
      // Add text before highlight
      if (h.start > lastIndex) {
        result.push(content.substring(lastIndex, h.start));
      }
      // Add highlighted text
      result.push(
        <span
          key={`h-${i}`}
          className="inline font-bold text-sm text-primary dark:text-white"
        >
          {content.substring(h.start, h.end)}
        </span>
      );
      lastIndex = h.end;
    });

    // Add remaining text
    if (lastIndex < content.length) {
      result.push(content.substring(lastIndex));
    }

    return (
      <p className="font-normal text-sm text-gray-600 dark:text-gray-300">
        {result}
      </p>
    );
  };

  const onReadNotification = async (noti: NotificationMessageDto) => {
    if (!businessInfo) return;
    if (!noti.isSeen) {
      try {
        await notiSvc.seenNotification(businessInfo.id, noti.id);
        setSeenNoti(noti.id);
      } catch (err) {
        const appErr = new AppError(err);
        appErr.logError();
      }
    }
    if (noti.actionUrl && noti.actionUrl.length > 0) {
      router.push(noti.actionUrl);
    }
  };

  return {
    onReadNotification,
    generateContent,
  };
};
